import {
  Alert,
  Item,
  ItemHeader,
  ItemTitle,
  ScrollArea,
  Sentinel,
  Skeleton,
  Spinner,
  useInfiniteScroll,
} from '@ibsys/kit';
import { FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import type { KnowledgeNodeInSet } from '@/entities/knowledge-set';
import { useAppNavigate } from '@/shared/lib/ts-router';
import { cn } from '@/shared/lib';

type Props = {
  items: KnowledgeNodeInSet[];
  isLoading: boolean;
  hasNextPage: boolean;
  onLoadMore: () => void;
};

export const KnowledgeList: FC<Props> = ({ items, isLoading, hasNextPage, onLoadMore }) => {
  const { t } = useTranslation('knowledge');
  const { id } = useParams<{ id?: string }>();
  const navigate = useAppNavigate();
  const viewportRef = useRef<HTMLDivElement>(null);

  const { sentinelRef } = useInfiniteScroll({
    onLoadMore,
    hasMore: hasNextPage,
    isLoading: false,
    threshold: 500,
    root: viewportRef,
  });

  useEffect(() => {
    /* Авто-переход только на десктопе (>= lg), на мобильном показываем список */
    if (!id && items.length > 0 && !isLoading && window.innerWidth >= 1024) {
      navigate('/knowledge/:id', { id: items[0].id.toString() }, { replace: true, saveParams: true });
    }
  }, [id, items, isLoading, navigate]);

  const handleSelect = (nodeId: number) => {
    navigate('/knowledge/:id', { id: nodeId.toString() }, { saveParams: true });
  };

  if (isLoading && items.length === 0) {
    return (
      <div className="flex flex-col gap-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-12" />
        ))}
      </div>
    );
  }

  if (!items.length) {
    return <Alert className="text-muted-foreground p-4" title={t('listIsEmpty')} />;
  }

  return (
    <ScrollArea viewPortProps={{ ref: viewportRef }}>
      <ul className="flex flex-col gap-2">
        {items.map((node) => {
          const isActive = id === node.id.toString();
          return (
            <li key={node.id} role="button" tabIndex={-1} onClick={() => handleSelect(node.id)}>
              <Item
                variant={isActive ? 'muted' : 'outline'}
                className={cn('hover:bg-muted cursor-pointer overflow-x-hidden', node.isDeleted && 'opacity-50')}
              >
                <ItemHeader>
                  <ItemTitle className="text-sm font-medium">{node.name}</ItemTitle>
                </ItemHeader>
              </Item>
            </li>
          );
        })}
        <Sentinel ref={sentinelRef}>
          {hasNextPage && (
            <div className="w-full flex justify-center py-6">
              <Spinner className="size-8" />
            </div>
          )}
        </Sentinel>
      </ul>
    </ScrollArea>
  );
};
